import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { tenantRoutingFixture } from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-tenant-routing.md
 * Endpoints: /v1/admin/tenants/:tenantId/routing (GET/PUT/DELETE)
 */

/** GET /v1/admin/tenants/:tenantId/routing — Defaults + Overrides je Kind */
export function useNexusTenantRouting(tenantId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-tenant-routing', tenantId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!tenantId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return tenantRoutingFixture(tenantId);
      }
      const res = await nexusFetch(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/routing`);
      // Toleriert mehrere Wrapper-Variationen
      const rows = res?.routing ?? res?.rows ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { rows, raw: res };
    },
    staleTime: 30_000,
  });
}

/** PUT /v1/admin/tenants/:tenantId/routing/:kind — Override setzen */
export function useSetNexusTenantRouting() {
  return useApiMutation({
    live: (_fetch, { tenantId, kind, plugin_id }) =>
      nexusFetch(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/routing/${encodeURIComponent(kind)}`, {
        method: 'PUT',
        body: { plugin_id },
      }),
    fixture: () => ({ ok: true }),
    invalidate: ['nexus-tenant-routing'],
    successMessage: (_, { kind, plugin_id }) => `${kind}: → ${plugin_id} gesetzt.`,
  });
}

/** DELETE /v1/admin/tenants/:tenantId/routing/:kind — Override entfernen */
export function useClearNexusTenantRouting() {
  return useApiMutation({
    live: (_fetch, { tenantId, kind }) =>
      nexusFetch(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/routing/${encodeURIComponent(kind)}`, {
        method: 'DELETE',
      }),
    fixture: () => ({ ok: true }),
    invalidate: ['nexus-tenant-routing'],
    successMessage: (_, { kind }) => `${kind}: Override entfernt — Default greift wieder.`,
  });
}
